import type * as React from 'react'
import { useQuery } from '@connectrpc/connect-query'
import { t } from '@lingui/core/macro'
import { Trans } from '@lingui/react/macro'

import type { ListSubscribersResponse_Subscriber } from '@jotta/grpc-connect-all/album'
import { useGetCustomer } from '@jotta/grpc-connect-client/customer'
import {
  getAlbum,
  listSubscribers,
} from '@jotta/grpc-connect-openapi/albumQuery'
import type { Avatar as AvatarType } from '@jotta/types/Avatar'
import { CollectionType } from '@jotta/types/schemas/AlbumSchema'
import { Avatar, AvatarGroup, AvatarGroupCount } from '@jotta/ui/Avatar'
import { Popover, PopoverContent, PopoverTrigger } from '@jotta/ui/Popover'
import Spinner from '@jotta/ui/Spinner'
import SvgLockKey from '@jotta/ui/SvgLockKey'
import SvgUsersShared from '@jotta/ui/SvgUsersShared'
import { useBreakpointIndex } from '@jotta/ui/useBreakpointIndex'
import { cn } from '@jotta/utils/css'

import { ActionShareAlbum } from '../../PhotoActions/ActionShareAlbum'
import { photoActionsApi } from '../../PhotoActions/PhotoActions.config'
import { OPTIMISTIC_SHARE_ID } from '../share.constants'

export function AlbumMembers({
  albumIdOrShareId,
  collectionType,
}: {
  albumIdOrShareId: string
  collectionType: CollectionType
}) {
  const { data: customer, isLoading: isCustomerLoading } = useGetCustomer()

  const { data: album } = useQuery(
    getAlbum,
    { albumIdOrShareId },
    {
      enabled: Boolean(albumIdOrShareId),
      select: data => data.album,
    },
  )

  // During an optimistic share mutation we only have a placeholder share id.
  // Keep using the album id until the real share id is returned.
  const subscribersAlbumIdOrShareId =
    album?.shareId && album.shareId !== OPTIMISTIC_SHARE_ID
      ? album.shareId
      : album?.id

  const { data: subscribers } = useQuery(
    listSubscribers,
    { albumIdOrShareId: subscribersAlbumIdOrShareId ?? '' },
    {
      enabled: Boolean(subscribersAlbumIdOrShareId),
      select: data => data.subscribers,
    },
  )

  const isShared = Boolean(album?.shareId)
  // Use the already-loaded album collection type from the parent so the share
  // button state does not wait on an extra query and flicker on first render.
  const isNormalAlbum = collectionType === CollectionType.NORMAL_ALBUM
  const isOwner = Boolean(
    customer?.username && customer.username === album?.owner,
  )
  const ownerName = album?.ownerFullName
  const avatar: AvatarType | undefined = album?.ownerAvatar

  if (!album || (isNormalAlbum && isCustomerLoading)) {
    return <Spinner size="medium" />
  }

  const ownerSummary = (
    <OwnerSummary ownerName={ownerName} ownerAvatar={avatar} />
  )

  const membersPopover = (
    <MembersPopover
      ownerName={ownerName}
      ownerAvatar={avatar}
      subscribers={subscribers}
    />
  )

  if (isOwner && isNormalAlbum) {
    return (
      <div className="flex items-center gap-3">
        <ActionShareAlbum
          albumId={albumIdOrShareId}
          action={{
            ...photoActionsApi.SHARE_ALBUM,
            icon: isShared ? SvgUsersShared : SvgLockKey,
            label: isShared ? t`Shared` : t`Private`,
          }}
          buttonClass="btn btn-tonal"
          username={album.owner}
        />
        {membersPopover}
      </div>
    )
  }

  return (
    <div className="flex flex-wrap items-center gap-3 md:flex-nowrap">
      <div className="flex items-center gap-3">{ownerSummary}</div>
      {membersPopover}
    </div>
  )
}

function MembersPopover({
  ownerName,
  ownerAvatar,
  subscribers,
}: {
  ownerName?: string
  ownerAvatar?: AvatarType
  subscribers?: ListSubscribersResponse_Subscriber[]
}) {
  if (!subscribers?.length) {
    return null
  }

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="group flex items-center rounded-full bg-transparent focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-m3-primary"
          aria-label={t`Show album members`}
        >
          <div className="flex rounded-full transition-colors duration-150 group-active:bg-surface-container-low group-hover:bg-surface-container-low">
            <SubscribersAvatars subscribers={subscribers} />
          </div>
        </button>
      </PopoverTrigger>

      <PopoverContent
        side="bottom"
        hasArrow={false}
        className="max-h-fit w-[357px] max-w-[92vw] overflow-visible rounded-[24px] bg-[rgba(231,232,238,0.92)] p-3 shadow-[0_6px_15px_rgba(0,0,0,0.05)] backdrop-blur-[2px]"
      >
        <div className="max-h-[324px] overflow-y-auto rounded-2xl bg-white">
          <SubscriberRow
            className="border-b border-light p-3"
            name={ownerName}
            avatar={ownerAvatar}
            badgeLabel={<Trans>Album owner</Trans>}
          />
          <SubscriberList subscribers={subscribers} />
        </div>
      </PopoverContent>
    </Popover>
  )
}

function SubscriberList({
  subscribers,
}: {
  subscribers: ListSubscribersResponse_Subscriber[]
}) {
  const subscribersWithUsername = subscribers.filter(
    (s): s is ListSubscribersResponse_Subscriber & { username: string } =>
      Boolean(s.username),
  )

  return (
    <>
      {subscribersWithUsername.map(s => (
        <SubscriberRow
          key={s.username}
          className="border-b border-light p-3 last:border-b-0"
          name={s.fullName}
          avatar={s.avatar}
        />
      ))}
    </>
  )
}

function SubscriberRow({
  className,
  name,
  avatar,
  badgeLabel,
}: React.ComponentProps<'div'> & {
  name?: string
  avatar?: AvatarType
  badgeLabel?: React.ReactNode
}) {
  if (!name && !avatar) {
    return null
  }

  return (
    <div className={cn('flex w-full items-center gap-3', className)}>
      {avatar && <Avatar className="size-10" avatar={avatar} />}

      <div className="flex min-w-0 flex-1 flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
        <span className="truncate text-sm font-normal text-on-surface sm:max-w-[55%]">
          {name}
        </span>

        {badgeLabel && (
          <span className="whitespace-nowrap text-sm text-on-surface sm:ml-4">
            {badgeLabel}
          </span>
        )}
      </div>
    </div>
  )
}

function OwnerSummary({
  ownerName,
  ownerAvatar,
}: {
  ownerName?: string
  ownerAvatar?: AvatarType
}) {
  if (!ownerName && !ownerAvatar) {
    return null
  }

  return (
    <>
      {ownerAvatar && (
        <Avatar
          className="size-10 border border-solid border-surface-container-low"
          avatar={ownerAvatar}
        />
      )}
      <div className="flex min-w-0 flex-col items-start">
        <div className="text-sm font-normal text-outline">
          <Trans>Shared by</Trans>
        </div>
        <div className="max-w-[50vw] truncate text-sm font-semibold text-on-surface sm:max-w-[155px] xl:max-w-[320px]">
          {ownerName}
        </div>
      </div>
    </>
  )
}

export function SubscribersAvatars({
  subscribers,
}: {
  subscribers?: ListSubscribersResponse_Subscriber[]
}) {
  const maxSubscriberAvatarCount = useBreakpointIndex() > 1 ? 4 : 2
  const list = subscribers ?? []
  const subscriberCount = list.length
  if (subscriberCount === 0) {
    return null
  }

  const visible = list.slice(0, maxSubscriberAvatarCount)
  const hasOverflow = subscriberCount > maxSubscriberAvatarCount
  const overflowCount = subscriberCount - maxSubscriberAvatarCount

  return (
    <AvatarGroup className="[&>[data-slot=avatar]+[data-slot=avatar]]:-ml-2">
      {visible.map((s, index) =>
        s.avatar ? (
          <Avatar
            key={s.username ?? index}
            avatar={s.avatar}
            className="size-10 border-2 border-solid border-surface-container-low"
          />
        ) : null,
      )}

      {hasOverflow && <AvatarGroupCount>+{overflowCount}</AvatarGroupCount>}
    </AvatarGroup>
  )
}
